import { LEAGUE_STATUS, type LeagueStatusKey } from '@league/shared/status';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui';
import { apiFetch, apiPost } from '../../lib/api';

/**
 * THE FOUR NATIONAL DIRECTORIES — every league, market, director and player.
 *
 * READ HERE, EDIT WHERE IT LIVES. Every row that can be changed links to the ordinary screen a
 * director would use, because the commissioner passes every authorization check a director does and
 * her edit is audited by the same path. A parallel set of commissioner-only editing screens would
 * be a second way into the same data, and the two would drift.
 *
 * EVERY DASH ON THESE PAGES MEANS "NOT MEASURED". A director who has never run a league has no
 * average fill; a market nobody has scheduled has no first session. Rendering those as zero would put
 * a judgement on the screen where the commissioner decides who to ring, and nothing here has the
 * basis for one.
 */

const DASH = '—';

function share(value: number | null): string {
  return value === null ? DASH : `${Math.round(value * 100)}%`;
}

function shortDate(iso: string | null): string {
  if (!iso) return DASH;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/* --------------------------------------------------------------------------------------------- */

interface LeagueRow {
  id: string;
  name: string;
  status: string;
  city: string | null;
  state: string | null;
  directorName: string | null;
  seats: number;
  cap: number;
  firstSessionAt: string | null;
  sessionsTotal: number;
  sessionsPlayed: number;
  sessionsScored: number;
}

export function HqLeagues() {
  const [status, setStatus] = useState('');

  const query = useQuery({
    queryKey: ['hq', 'leagues'],
    queryFn: () => apiFetch<{ leagues: LeagueRow[] }>('/api/hq/leagues'),
  });

  if (query.isPending) return <Loading />;

  const all = query.data?.leagues ?? [];
  const rows = status ? all.filter((row) => row.status === status) : all;
  const statuses = [...new Set(all.map((row) => row.status))];

  return (
    <Page
      title="All leagues"
      what="Every league in every market. Open one to change anything about it."
    >
      <div className="flex flex-wrap gap-2">
        <Pill active={status === ''} onClick={() => setStatus('')}>
          All {all.length}
        </Pill>
        {statuses.map((key) => (
          <Pill key={key} active={status === key} onClick={() => setStatus(key)}>
            {LEAGUE_STATUS[key as LeagueStatusKey]?.label ?? key}{' '}
            {all.filter((row) => row.status === key).length}
          </Pill>
        ))}
      </div>

      {rows.length === 0 ? (
        <Empty>No leagues match.</Empty>
      ) : (
        <Table label="Leagues" minWidth="46rem">
          <thead>
            <tr className="border-b border-stroke text-left">
              <Th>League</Th>
              <Th className="w-40">Director</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-32">Session one</Th>
              <Th className="w-24 pr-6 text-right">Roster</Th>
              <Th className="w-32 pr-2 text-right">Sessions scored</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((league) => (
              <tr key={league.id} className="border-b border-light align-top">
                <td className="py-3 pr-3">
                  {/*
                    The DIRECTOR's workspace, deliberately. It is the same screen, she passes the
                    same checks, and her change is audited the same way — a commissioner-only copy
                    would be a second way in.
                  */}
                  <Link
                    to={`/director/leagues/${league.id}`}
                    className="hover:underline underline-offset-4"
                  >
                    {league.name}
                  </Link>
                  <div className="text-[12px] text-ink-3">
                    {league.city ? `${league.city}, ${league.state}` : 'Market not recorded'}
                  </div>
                </td>
                <td className="py-3 pr-3">{league.directorName ?? DASH}</td>
                <td className="py-3 pr-3 text-[13px]">
                  {LEAGUE_STATUS[league.status as LeagueStatusKey]?.label ?? league.status}
                </td>
                <td className="tabular py-3 pr-3">{shortDate(league.firstSessionAt)}</td>
                <td className="tabular py-3 pr-6 text-right">
                  {league.seats} / {league.cap}
                </td>
                {/*
                  Out of the WHOLE league, not out of what has happened. "0 of 3" on a league
                  starting next month is plainly nothing-yet; "0 of 0" reads as a failure and
                  is the shape a completed league with future dates would have printed.
                */}
                <td
                  className={[
                    'tabular py-3 pr-2 text-right',
                    league.sessionsPlayed > league.sessionsScored ? 'text-rust' : '',
                  ].join(' ')}
                >
                  {league.sessionsScored} of {league.sessionsTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

/* --------------------------------------------------------------------------------------------- */

interface MarketRow {
  id: string;
  city: string;
  state: string;
  status: string;
  venues: number;
  leagues: number;
  activeLeagues: number;
  tileSets: number;
}

export function HqMarkets() {
  const query = useQuery({
    queryKey: ['hq', 'markets'],
    queryFn: () => apiFetch<{ markets: MarketRow[] }>('/api/hq/markets'),
  });

  if (query.isPending) return <Loading />;

  const rows = query.data?.markets ?? [];
  /* A city with no venue and no league is one somebody typed into the create-league form. */
  const untested = rows.filter((row) => row.leagues === 0);

  return (
    <Page title="Markets" what="Where the platform runs, and where it has only been asked about.">
      {untested.length > 0 ? (
        <p className="rounded-md border border-light bg-paper px-5 py-3 text-[13px] text-ink-2">
          {untested.length === 1 ? 'One city has' : `${untested.length} cities have`} never run a
          league. A director typed the name into the create-league form and nothing has happened
          there since.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <Empty>No markets yet.</Empty>
      ) : (
        <Table label="Markets" minWidth="38rem">
          <thead>
            <tr className="border-b border-stroke text-left">
              <Th>Market</Th>
              <Th className="w-28">State</Th>
              <Th className="w-24 text-right">Venues</Th>
              <Th className="w-28 text-right">Leagues</Th>
              <Th className="w-28 text-right">Running now</Th>
              <Th className="w-28 pr-2 text-right">Tile sets</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((market) => (
              <tr
                key={market.id}
                className={[
                  'border-b border-light',
                  market.leagues === 0 ? 'text-ink-2' : '',
                ].join(' ')}
              >
                <td className="py-3 pr-3">{market.city}</td>
                <td className="py-3 pr-3">{market.state}</td>
                <td className="tabular py-3 pr-3 text-right">{market.venues}</td>
                <td className="tabular py-3 pr-3 text-right">{market.leagues}</td>
                <td className="tabular py-3 pr-3 text-right">{market.activeLeagues}</td>
                {/* Nought sets in a market with a running league is the row that costs money. */}
                <td
                  className={[
                    'tabular py-3 pr-2 text-right',
                    market.activeLeagues > 0 && market.tileSets === 0 ? 'text-rust' : '',
                  ].join(' ')}
                >
                  {market.tileSets}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

/* --------------------------------------------------------------------------------------------- */

interface DirectorRow {
  id: string;
  name: string | null;
  email: string;
  status: 'invited' | 'active' | 'deactivated';
  leaguesRun: number;
  leaguesActive: number;
  averageFill: number | null;
  scoreEntry: number | null;
  openTasks: number;
  overdueTasks: number;
}

export function HqDirectors() {
  const queryClient = useQueryClient();

  /*
    ⚠ REMOVING SOMEBODY IS THE OTHER END OF SELF-SERVE. While every director was approved by hand
    the decision was at the front; now anybody can join, so the only remedy is afterwards.
  */
  const standing = useMutation({
    mutationFn: (input: { id: string; action: 'remove' | 'reinstate'; reason: string }) =>
      apiPost(`/api/hq/directors/${input.id}/${input.action}`, { reason: input.reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hq'] }),
  });

  /*
    ⚠ INLINE, NOT A BROWSER PROMPT. `window.prompt` was the first version of this and it is the
    wrong shape twice over: unbranded chrome on a screen that is otherwise the product, and it asks
    for a reason without ever saying what will happen to her. The house pattern is the seating
    board's — publishing NAMES anybody left standing rather than asking "are you sure?".
  */
  const [acting, setActing] = useState<{ id: string; reason: string } | null>(null);

  const query = useQuery({
    queryKey: ['hq', 'directors'],
    queryFn: () => apiFetch<{ directors: DirectorRow[] }>('/api/hq/directors'),
  });

  if (query.isPending) return <Loading />;

  const rows = query.data?.directors ?? [];

  return (
    <Page title="Directors" what="Who runs what, and how it is going.">
      {rows.length === 0 ? (
        <Empty>No directors yet. Anybody can sign up, and appears here once they have.</Empty>
      ) : (
        <Table label="Directors" minWidth="48rem">
          <thead>
            <tr className="border-b border-stroke text-left">
              <Th>Director</Th>
              <Th className="w-24 text-right">Leagues</Th>
              <Th className="w-28 text-right">Running now</Th>
              <Th className="w-28 text-right">Average fill</Th>
              <Th className="w-32 text-right">Scores same day</Th>
              <Th className="w-24 text-right">Overdue</Th>
              <Th className="w-28 pr-2 text-right">Standing</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((director) => (
              <tr key={director.id} className="border-b border-light align-top">
                <td className="py-3 pr-3">
                  <div>{director.name ?? director.email}</div>
                  <div className="text-[12px] text-ink-3">{director.email}</div>
                </td>
                <td className="tabular py-3 pr-3 text-right">{director.leaguesRun}</td>
                <td className="tabular py-3 pr-3 text-right">{director.leaguesActive}</td>
                {/*
                  A dash, not a zero. Somebody who has never run a league has no fill rate, and
                  zero would rank her below somebody genuinely struggling — on the screen where
                  the decision about who to ring gets made.
                */}
                <td className="tabular py-3 pr-3 text-right">{share(director.averageFill)}</td>
                <td className="tabular py-3 pr-3 text-right">{share(director.scoreEntry)}</td>
                <td
                  className={[
                    'tabular py-3 pr-3 text-right',
                    director.overdueTasks > 0 ? 'text-rust' : 'text-ink-3',
                  ].join(' ')}
                >
                  {director.overdueTasks}
                </td>
                <td className="py-3 pr-2 text-right align-top">
                  {/*
                    The word first, the action second. A row that only offered a button would make
                    somebody read the button to find out what is true of her.
                  */}
                  {director.status === 'deactivated' ? (
                    <span className="block text-[12px] text-rust">Removed</span>
                  ) : null}
                  <Button
                    variant="plain"
                    onClick={() =>
                      setActing(acting?.id === director.id ? null : { id: director.id, reason: '' })
                    }
                    className={
                      director.status === 'deactivated' ? undefined : 'hover:border-rust/35 hover:text-rust'
                    }
                  >
                    {director.status === 'deactivated' ? 'Let back in' : 'Remove'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/*
        Outside the table on purpose: a form inside a row has to fight the column widths, and the
        reason box would be two inches wide in the last column.
      */}
      {acting && rows.some((row) => row.id === acting.id) ? (
        <ConfirmStanding
          director={rows.find((row) => row.id === acting.id)!}
          reason={acting.reason}
          busy={standing.isPending}
          onReason={(reason) => setActing({ ...acting, reason })}
          onCancel={() => setActing(null)}
          onConfirm={() => {
            const director = rows.find((row) => row.id === acting.id)!;
            standing.mutate(
              {
                id: acting.id,
                action: director.status === 'deactivated' ? 'reinstate' : 'remove',
                reason: acting.reason,
              },
              { onSuccess: () => setActing(null) },
            );
          }}
        />
      ) : null}
    </Page>
  );
}

/* --------------------------------------------------------------------------------------------- */

interface PlayerRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  surnamePublic: boolean;
  hasAccount: boolean;
  leagues: number;
  markets: number;
  points: number;
  sessionsWon: number;
}

export function HqPlayers() {
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');

  const query = useQuery({
    queryKey: ['hq', 'players', submitted],
    queryFn: () =>
      apiFetch<{ players: PlayerRow[] }>(
        `/api/hq/players${submitted ? `?search=${encodeURIComponent(submitted)}` : ''}`,
      ),
  });

  const rows = query.data?.players ?? [];

  return (
    <Page
      title="Players"
      what="Everybody the platform knows. The only screen where somebody playing in two markets is one person."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(search);
        }}
        className="flex flex-wrap items-center gap-2"
      >
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name or email"
          aria-label="Search players"
          className="h-11 w-64 rounded-md border border-control-border bg-chalk px-3 text-[14px]"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
        {submitted ? (
          <Button
            variant="secondary"
            onClick={() => {
              setSearch('');
              setSubmitted('');
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      {query.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>{submitted ? 'Nobody matches that.' : 'No players yet.'}</Empty>
      ) : (
        <Table label="Players" minWidth="44rem">
          <thead>
            <tr className="border-b border-stroke text-left">
              <Th>Player</Th>
              <Th className="w-24 text-right">Leagues</Th>
              <Th className="w-24 text-right">Markets</Th>
              <Th className="w-24 pr-6 text-right">Points</Th>
              <Th className="w-28 pr-6 text-right">Sessions won</Th>
              <Th className="w-32">Account</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((player) => (
              <tr key={player.id} className="border-b border-light align-top">
                <td className="py-3 pr-3">
                  <Link to={`/hq/players/${player.id}`} className="hover:underline underline-offset-4">
                    {player.firstName} {player.lastName}
                  </Link>
                  <div className="text-[12px] text-ink-3">{player.email}</div>
                </td>
                <td className="tabular py-3 pr-3 text-right">{player.leagues}</td>
                {/* More than one market is the fact nobody else on the platform can see. */}
                <td
                  className={[
                    'tabular py-3 pr-3 text-right',
                    player.markets > 1 ? 'font-semibold' : '',
                  ].join(' ')}
                >
                  {player.markets}
                </td>
                <td className="tabular py-3 pr-6 text-right">{player.points}</td>
                <td className="tabular py-3 pr-6 text-right">{player.sessionsWon}</td>
                <td className="py-3 text-[13px] text-ink-2">
                  {player.hasAccount ? 'Signs in' : 'No account'}
                  {!player.surnamePublic ? (
                    <div className="text-[12px] text-ink-3">Surname off the public page</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

/* --------------------------------------------------------------------------------------------- */

function Page({
  title,
  what,
  children,
}: {
  title: string;
  what: string;
  children: React.ReactNode;
}) {
  return (
    /*
      Capped rather than full-bleed. A six-column table stretched across a 1900px monitor puts
      three feet of empty space between a market's name and its numbers, and reading across that
      gap is how somebody attributes a count to the wrong row.
    */
    <div className="flex max-w-[76rem] flex-col gap-6 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">{title}</h1>
        <p className="mt-1 max-w-[46rem] text-[14px] text-ink-2">{what}</p>
      </header>
      {children}
    </div>
  );
}

function Loading() {
  return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
      {children}
    </p>
  );
}

function Table({
  minWidth,
  label,
  children,
}: {
  minWidth: string;
  /** What the table holds, for the scroll region's accessible name. */
  label: string;
  children: React.ReactNode;
}) {
  return (
    /*
      FOCUSABLE, BECAUSE IT SCROLLS. A table wider than the panel scrolls sideways and a mouse can
      drag it; a keyboard cannot reach it unless the region takes focus.

      Three of the four directories got away with this by accident — their rows carry links, so a
      tab key lands inside and the scroll comes with it. Markets have no link to anywhere, so that
      table was unreachable. Fixed on the shared wrapper rather than on the one that failed, because
      the next read-only table would have had the same problem and nobody would have known why.
    */
    <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={`${label}, scrollable`}>
      <table className="w-full border-collapse text-[14px]" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-3.5 py-1.5 text-[13px]',
        active
          ? 'border-deep bg-deep text-on-deep'
          : 'border-stroke hover:bg-sand',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={[
        'pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}

/* --------------------------------------------------------------------------------------------- */

/**
 * WHAT REMOVING HER ACTUALLY DOES, said before it happens rather than after.
 *
 * The fear this action creates is that it destroys her work, so the first thing it says is that it
 * does not. Everything she ran stays readable; what stops is signing in.
 */
function ConfirmStanding({
  director,
  reason,
  busy,
  onReason,
  onCancel,
  onConfirm,
}: {
  director: DirectorRow;
  reason: string;
  busy: boolean;
  onReason: (reason: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const removing = director.status !== 'deactivated';
  const who = director.name ?? director.email;

  return (
    <div className="mt-5 rounded-md border border-light bg-paper p-5">
      <p className="text-[14px] font-medium">
        {removing ? `Remove ${who}?` : `Let ${who} back in?`}
      </p>
      <p className="mt-1.5 max-w-[46rem] text-[13px] leading-relaxed text-ink-2">
        {removing
          ? 'They will not be able to sign in. Nothing is deleted — their leagues, those leagues’ standings and every player record stay exactly where they are, and you can still read them. Their membership is cancelled so they stop being charged.'
          : 'They will be able to sign in again. Their membership is not restored, so they choose how they pay like anybody coming back.'}
      </p>

      <label className="mt-4 block text-[13px] font-medium" htmlFor="standing-reason">
        Why
      </label>
      {/* The only other place this platform insists on free text is resolving an escalation. */}
      <p className="text-[12.5px] text-ink-3">Required. Somebody will ask about this later.</p>
      <textarea
        id="standing-reason"
        value={reason}
        onChange={(event) => onReason(event.target.value)}
        rows={3}
        className="mt-2 w-full max-w-[46rem] rounded-md border border-control-border bg-paper p-3 text-[14px]"
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          variant={removing ? 'destructive' : 'primary'}
          disabled={!reason.trim() || busy}
          onClick={onConfirm}
        >
          {busy ? 'Saving…' : removing ? 'Remove director' : 'Let them back in'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
